import React, { useState } from 'react'
import { useSheet } from '@/contexts/SheetContext'
import { AppHTTPService } from '@/api/http-service'
import MonacoEditor from '@monaco-editor/react'
import { useQueryClient } from '@tanstack/react-query'
import { useRulesContext, getPendingRules } from '@/contexts/RulesContext'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Badge,
  Switch,
  Icon,
  Text,
  cn,
} from '@flatfile/baseline'

interface RulesPanelProps {
  onClose: () => void
  sheetId: string
  editRuleId?: string
}

export default function RulesPanel({
  onClose,
  sheetId,
  editRuleId,
}: RulesPanelProps) {
  const { sheet } = useSheet()
  const rules = sheet?.rules ?? []
  const [expandedRule, setExpandedRule] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { setPendingRules } = useRulesContext()

  // Create a unique key for each rule based on type and destination fields
  const createRuleKey = (rule: any) => {
    const destFields = JSON.stringify(rule.destinationFields || [])
    const type = rule.type || 'unknown'
    // Use toolId if available, otherwise create key from type and fields
    return rule.toolId || `${type}:${destFields}`
  }

  // Deduplicate rules by unique key
  const uniqueRules = Array.from(
    new Map(rules.map((rule: any) => [createRuleKey(rule), rule])).values()
  )
  const uniqueEnabledRules = uniqueRules.filter(
    (rule: any) => rule.disabled !== true
  )

  const handleToggleRule = async (ruleKey: string, checked: boolean) => {
    if (!sheet?.id) return
    // Update ALL rules, not just uniqueRules
    const updatedRules = rules.map((rule: any) =>
      createRuleKey(rule) === ruleKey ? { ...rule, disabled: !checked } : rule
    )
    try {
      await AppHTTPService.client.sheets.update(sheet.id, {
        rules: updatedRules,
      })
      await queryClient.invalidateQueries({ queryKey: ['sheets', sheetId] })
      // Update pendingRules context to match enabled rules
      const enabledRules = updatedRules.filter(
        (rule: any) => rule.disabled !== true
      )
      setPendingRules(enabledRules as any)
      // Also invalidate all records to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['allRecords', sheetId] })
      // Wait a bit for the backend to process, then trigger table refresh
      setTimeout(() => {
        window.postMessage({ type: 'refetchRecords' })
      }, 150)
    } catch (error) {
      console.error('Error updating rule:', error)
    }
  }

  const handleDeleteRule = async (ruleKey: string) => {
    if (!sheet?.id) return
    // Delete from ALL rules, not just uniqueRules
    const updatedRules = rules.filter(
      (rule: any) => createRuleKey(rule) !== ruleKey
    )
    try {
      await AppHTTPService.client.sheets.update(sheet.id, {
        rules: updatedRules,
      })
      await queryClient.invalidateQueries({ queryKey: ['sheets', sheetId] })
      // Update pendingRules context to match enabled rules
      const enabledRules = updatedRules.filter(
        (rule: any) => rule.disabled !== true
      )
      setPendingRules(enabledRules as any)
      // Also invalidate all records to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['allRecords', sheetId] })
      // Wait a bit for the backend to process, then trigger table refresh
      setTimeout(() => {
        window.postMessage({ type: 'refetchRecords' })
      }, 150)
    } catch (error) {
      console.error('Error deleting rule:', error)
    }
  }

  const getRuleTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      split: 'Split',
      'apply-changes': 'Apply Changes',
      'add-field': 'Add Field',
      'remove-field': 'Remove Field',
      'rename-field': 'Rename Field',
      'merge-fields': 'Merge Fields',
      function: 'Transform',
    }
    return typeMap[type] || type
  }

  return (
    <div className='h-full flex flex-col bg-background'>
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-3 border-b border-border'>
        <div className='flex items-center gap-2'>
          <Icon name='book' className='w-4 h-4 text-muted-foreground' />
          <Text className='text-base font-medium'>Rules</Text>
          <Badge variant='secondary' className='text-xs ml-1'>
            {uniqueEnabledRules.length} / {uniqueRules.length}
          </Badge>
        </div>
        <Button
          variant='ghost'
          size='sm'
          onClick={onClose}
          className='h-8 w-8 p-0 text-muted-foreground hover:text-foreground'
        >
          <Icon name='cross-large' className='h-4 w-4' />
        </Button>
      </div>

      {/* Content */}
      <div className='flex-1 overflow-auto p-3'>
        {uniqueRules.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-8 text-center'>
            <Icon
              name='book'
              className='w-10 h-10 text-muted-foreground/50 mb-3'
            />
            <Text className='text-muted-foreground mb-1'>
              No rules configured yet
            </Text>
            <Text className='text-sm text-muted-foreground/70'>
              Rules will appear here when you make changes to your data
            </Text>
          </div>
        ) : (
          <div className='space-y-2'>
            {uniqueRules.map((rule: any) => {
              const ruleKey = createRuleKey(rule)
              const isExpanded = expandedRule === ruleKey
              const isEnabled = rule.disabled !== true

              return (
                <Card key={ruleKey} className='border border-border'>
                  <CardHeader className='pb-2'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2'>
                        <Badge
                          variant='outline'
                          className='text-xs font-medium px-2 py-0.5'
                        >
                          {getRuleTypeLabel(rule.type)}
                        </Badge>
                        <div className='flex items-center gap-2'>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) =>
                              handleToggleRule(ruleKey, checked)
                            }
                            className='data-[state=checked]:bg-foreground data-[state=unchecked]:bg-muted'
                          />
                          <Text className='text-sm text-muted-foreground'>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </Text>
                        </div>
                      </div>
                      <div className='flex items-center gap-0.5'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() =>
                            setExpandedRule(isExpanded ? null : ruleKey)
                          }
                          className='h-7 px-2 text-xs text-muted-foreground hover:text-foreground'
                        >
                          <Icon
                            name={isExpanded ? 'expand' : 'chevron-down-small'}
                            className='w-3 h-3 mr-1'
                          />
                          {isExpanded ? 'Hide' : 'Show'} JSON
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handleDeleteRule(ruleKey)}
                          className='h-7 px-2 text-xs text-muted-foreground hover:text-destructive'
                        >
                          <Icon name='trash-can' className='w-3 h-3 mr-1' />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className='pt-0'>
                    {rule.summary && (
                      <Text className='text-sm text-muted-foreground mb-2'>
                        {rule.summary}
                      </Text>
                    )}

                    {(rule.sourceField ||
                      (rule.destinationFields &&
                        rule.destinationFields.length > 0)) && (
                      <div className='space-y-1 mb-2'>
                        {rule.sourceField && (
                          <div className='flex items-center gap-2'>
                            <Text className='text-xs text-muted-foreground/70 font-medium'>
                              Source:
                            </Text>
                            <Badge
                              variant='secondary'
                              className='text-xs px-1.5 py-0.5'
                            >
                              {rule.sourceField}
                            </Badge>
                          </div>
                        )}
                        {rule.destinationFields &&
                          rule.destinationFields.length > 0 && (
                            <div className='flex items-center gap-2'>
                              <Text className='text-xs text-muted-foreground/70 font-medium'>
                                Destination:
                              </Text>
                              <div className='flex flex-wrap gap-1'>
                                {rule.destinationFields.map(
                                  (field: string, index: number) => (
                                    <Badge
                                      key={index}
                                      variant='secondary'
                                      className='text-xs px-1.5 py-0.5'
                                    >
                                      {field}
                                    </Badge>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    {isExpanded && (
                      <div className='mt-3 border border-border rounded-md overflow-hidden'>
                        <MonacoEditor
                          height='280px'
                          defaultLanguage='json'
                          value={JSON.stringify(rule, null, 2)}
                          theme='vs-dark'
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            fontSize: 12,
                            lineNumbers: 'off',
                            folding: false,
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            padding: { top: 8, bottom: 8 },
                          }}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
